import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Check, Loader2 } from "lucide-react";
import SiteNav from "@/components/SiteNav";
import { PRICING } from "@/constants/testIds";

const sym = { INR: "₹", USD: "$" };

export default function Pricing() {
  const [plans, setPlans] = useState(null);

  useEffect(() => {
    api.get("/pricing").then(({ data }) => setPlans(data.plans)).catch(() => {});
  }, []);

  if (!plans) {
    return <div className="min-h-screen bg-slate-50"><SiteNav /><div className="grid place-items-center py-40"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div></div>;
  }

  const order = ["individual_inr", "individual_usd", "institution_inr", "institution_usd"];

  return (
    <div className="min-h-screen bg-slate-50">
      <SiteNav />
      <section className="max-w-6xl mx-auto px-5 py-16">
        <p className="eyebrow">Pricing</p>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mt-2 max-w-3xl">Simple, transparent pricing for individuals and institutions.</h1>
        <p className="text-slate-500 mt-4 max-w-2xl text-lg">One price per test. Each Test ID works for either MSIS or MSOS — and can be used only once.</p>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
          {order.map((key) => {
            const p = plans[key];
            if (!p) return null;
            const isInstitution = p.type === "institution";
            const featured = key === "individual_inr";
            return (
              <div key={key} data-testid={PRICING.planCard}
                className={`rounded-xl border p-7 flex flex-col ${featured ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200"}`}>
                <div className={`eyebrow ${featured ? "text-blue-300" : ""}`}>{p.label}</div>
                {isInstitution && p.slabs ? (
                  <>
                    <div className="text-2xl font-bold mt-3">Volume pricing</div>
                    <table className="w-full text-sm mt-4">
                      <tbody>
                        {p.slabs.map((s, i) => {
                          const lo = i === 0 ? 1 : p.slabs[i - 1].upTo + 1;
                          const label = s.upTo ? `${lo}–${s.upTo}` : `${lo}+`;
                          return (
                            <tr key={i} className={featured ? "border-b border-white/10" : "border-b border-slate-100"}>
                              <td className="py-1.5">{label} tests</td>
                              <td className="py-1.5 text-right font-semibold">{sym[p.currency]}{s.amount}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </>
                ) : (
                  <>
                    <div className="mt-3 flex items-end gap-1">
                      <span className="text-4xl font-bold">{sym[p.currency]}{p.amount}</span>
                      <span className={`text-sm mb-1 ${featured ? "text-slate-300" : "text-slate-500"}`}>/ test</span>
                    </div>
                    <ul className={`mt-5 space-y-2 text-sm ${featured ? "text-slate-300" : "text-slate-600"}`}>
                      {["Single-use Test ID", "OTP-verified email", "Detailed report (on-screen + emailed)", "Works for MSIS or MSOS"].map((f) => (
                        <li key={f} className="flex items-center gap-2"><Check className={`w-4 h-4 ${featured ? "text-blue-400" : "text-blue-600"}`} /> {f}</li>
                      ))}
                    </ul>
                  </>
                )}
                <div className="flex-1" />
                <Link to={`/checkout?plan=${key}`} className="mt-6">
                  <Button data-testid={PRICING.buyButton}
                    className={`w-full rounded-full ${featured ? "bg-white text-slate-900 hover:bg-slate-100" : "bg-blue-700 hover:bg-blue-800 text-white"}`}>
                    {isInstitution ? "Subscribe" : "Buy Test"}
                  </Button>
                </Link>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
